import React, { useState, useEffect } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase';
import { AlertTriangle, Crown } from 'lucide-react';
import { Button } from './ui/Button';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';

export const DocumentLimitWarning: React.FC = () => {
  const { user } = useAuthStore();
  const { subscription, getDocumentLimit } = useSubscription();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [documentCount, setDocumentCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && subscription) {
      loadDocumentCount();
    }
  }, [user, subscription]);

  const loadDocumentCount = async () => {
    try {
      setLoading(true);
      const { count, error } = await supabase
        .from('documents')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user?.id);

      if (error) throw error;
      setDocumentCount(count || 0);
    } catch (error) {
      console.error('Error loading document count:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !subscription) return null;

  const limit = getDocumentLimit();
  if (limit === -1) return null; // Unlimited

  const percentage = (documentCount / limit) * 100;
  const isNearLimit = percentage >= 80;
  const isAtLimit = documentCount >= limit;

  if (!isNearLimit) return null;

  return (
    <div className={`p-4 rounded-lg border-2 ${
      isAtLimit 
        ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-600' 
        : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-600'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <AlertTriangle className={`w-5 h-5 ${
            isAtLimit ? 'text-red-500' : 'text-yellow-500'
          }`} />
          <div>
            <h4 className="font-bold">
              {isAtLimit ? t('docLimit.reached') : t('docLimit.approaching')}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('docLimit.usage', { used: documentCount, limit: limit, percent: Math.round(percentage) })}
            </p>
          </div>
        </div>
        <Button
          onClick={() => navigate('/pricing')}
          size="sm"
          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
        >
          <Crown className="w-4 h-4 mr-2" />
          {t('docLimit.upgrade')}
        </Button>
      </div>
      
      {/* Progress bar */}
      <div className="mt-3 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div 
          className={`h-2 rounded-full transition-all duration-300 ${
            isAtLimit 
              ? 'bg-red-500' 
              : percentage >= 90 
              ? 'bg-yellow-500' 
              : 'bg-blue-500'
          }`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
};